import { toast } from '@parcel/ui';
import * as React from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import {
  type ApiContactReach,
  type ApiEnrichStatus,
  type ApiRevealKind,
  LOOKUP_RETRY_DAYS,
  type ReachSource,
  type RevealResult,
  formatCredits,
  shortDate,
  shownEmail,
  shownPhone,
  useRevealContact,
  windowEnd,
} from '../../lib/enrich.js';
import { CopyButton } from '../entity-drawer/copy-button.js';
import { InsufficientCreditsNotice } from './credits-notice.js';
import { useEnrichScope } from './enrich-scope.js';
import { RevealButton, RevealIcon, RevealMissed, Tip } from './reveal-button.js';

export interface ReachContact extends ReachSource {
  id: string;
  name: string;
  linkedin_url: string | null;
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/** When a lookup of this kind last found nothing, unless Parcel holds one or this workspace revealed one since. */
export function revealMissedAt(contact: ReachContact, kind: 'email' | 'profile'): string | null {
  const reach = contact.reach;
  const at = reach?.missed?.[kind] ?? null;
  if (!reach || !at || reach.available[kind] || reach.revealed.includes(kind)) return null;
  return kind === 'email' && shownEmail(contact).value ? null : at;
}

/** What a detail field can offer for one kind: nothing when enrichment, its price or (for a phone) its provider is off, or a recent lookup found nothing. */
export function revealOffered(
  contact: ReachContact,
  kind: ApiRevealKind,
  status: ApiEnrichStatus | null | undefined,
  workspaceId: string | undefined,
): boolean {
  if (!status?.enabled || !workspaceId || !contact.reach) return false;
  if (contact.reach.revealed.includes(kind)) return false;
  if (kind !== 'phone' && revealMissedAt(contact, kind)) return false;
  if (kind === 'email') return !shownEmail(contact).value && status.prices.email_reveal != null;
  if (kind === 'phone') {
    return !shownPhone(contact) && status.phone && status.prices.phone_reveal != null;
  }
  return status.prices.profile_reveal != null;
}

/** A lookup that found no email: the mail icon with a red cross, and when it was checked on hover. */
export function EmailMissed({ checkedAt }: { checkedAt: string }) {
  return (
    <RevealMissed
      kind="email"
      label="No email"
      tip={`Checked ${shortDate(checkedAt)}. Try again ${shortDate(windowEnd(checkedAt, LOOKUP_RETRY_DAYS).toISOString())}.`}
    />
  );
}

const dash = <span className="text-muted-foreground">—</span>;

/** The email on its own: a mail icon (green check when verified) and the address, else a one-click reveal, a remembered miss, or a dash. */
export function EmailCell({
  contact,
  variant,
}: {
  contact: ReachContact;
  /** A detail field carries a copy button; a table row stays one line. */
  variant: 'detail' | 'table';
}) {
  const { workspaceId, status } = useEnrichScope();
  const email = shownEmail(contact);
  if (email.value) {
    const verified = email.status === 'verified';
    const icon = <RevealIcon kind="email" mark={verified ? 'held' : null} />;
    return (
      <span className="ph-no-capture flex min-w-0 items-center gap-2">
        {verified ? (
          <Tip tip="Verified in Parcel">
            <span className="inline-flex shrink-0" data-testid="email-verified">
              {icon}
            </span>
          </Tip>
        ) : (
          icon
        )}
        <a
          href={`mailto:${email.value}`}
          onClick={stop}
          className="min-w-0 truncate underline-offset-4 hover:underline"
        >
          {email.value}
        </a>
        {variant === 'detail' ? <CopyButton value={email.value} label="Copy email" /> : null}
      </span>
    );
  }
  if (revealOffered(contact, 'email', status, workspaceId)) {
    return <RevealAction contact={contact} kind="email" />;
  }
  const missed = revealMissedAt(contact, 'email');
  return missed ? <EmailMissed checkedAt={missed} /> : dash;
}

/** The phone on its own: the number, else the reveal, shown disabled until a phone provider exists. */
export function PhoneCell({ contact }: { contact: ReachContact }) {
  const { workspaceId, status } = useEnrichScope();
  const phone = shownPhone(contact);
  if (phone) {
    return (
      <a href={`tel:${phone}`} onClick={stop} className="ph-no-capture truncate hover:underline">
        {phone}
      </a>
    );
  }
  const canAsk =
    status?.enabled && workspaceId && contact.reach && !contact.reach.revealed.includes('phone');
  return canAsk ? <RevealAction contact={contact} kind="phone" /> : dash;
}

/** One kind's reveal button on its own, for a detail field; render it only where revealOffered says so. */
export function RevealAction({ contact, kind }: { contact: ReachContact; kind: ApiRevealKind }) {
  const { workspaceId, status } = useEnrichScope();
  if (!status || !workspaceId || !contact.reach) return null;
  return (
    <RevealControls
      contactId={contact.id}
      reach={contact.reach}
      workspaceId={workspaceId}
      status={status}
      kinds={[kind]}
    />
  );
}

const RESULT_COPY: Partial<Record<RevealResult['kind'], (what: string) => string>> = {
  miss: (what) => `No ${what} found · not charged`,
  unavailable: () => 'Lookup unavailable · not charged',
};

function RevealControls({
  contactId,
  reach,
  workspaceId,
  status,
  kinds,
}: {
  contactId: string;
  reach: ApiContactReach;
  workspaceId: string;
  status: ApiEnrichStatus;
  kinds: readonly ApiRevealKind[];
}) {
  const offerEmail = kinds.includes('email');
  const offerPhone = kinds.includes('phone');
  // One reveal at a time per row: mutate() only reports the latest call's outcome.
  const reveal = useRevealContact(workspaceId);
  const [results, setResults] = React.useState<Partial<Record<ApiRevealKind, RevealResult>>>({});
  const pendingKind = reveal.isPending ? reveal.variables?.kind : undefined;
  const { prices } = status;
  const run = (kind: ApiRevealKind) =>
    reveal.mutate(
      { contactId, kind },
      {
        onSuccess: (result) => setResults((current) => ({ ...current, [kind]: result })),
        onError: (error) =>
          toast.error(apiErrorMessage(error, "Couldn't look this up. Try again.")),
      },
    );

  const outcomes = Object.values(results).map((result) => result?.kind);
  if (outcomes.includes('insufficient_credits')) return <InsufficientCreditsNotice compact />;
  if (outcomes.includes('spend_limit')) {
    return <InsufficientCreditsNotice compact reason="spend_limit" />;
  }

  const settled = (kind: ApiRevealKind, what: string) => {
    const copy = RESULT_COPY[results[kind]?.kind ?? 'found'];
    return copy ? <span className="text-xs text-muted-foreground">{copy(what)}</span> : null;
  };
  const phoneReady = status.phone && prices.phone_reveal != null;
  const offerProfile = kinds.includes('profile') && prices.profile_reveal != null;

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {offerEmail && prices.email_reveal != null
        ? (settled('email', 'email') ?? (
            <RevealButton
              kind="email"
              label="Access email"
              tip={`Uses ${formatCredits(prices.email_reveal)} per email.`}
              held={reach.available.email}
              pending={pendingKind === 'email'}
              disabled={reveal.isPending}
              onReveal={() => run('email')}
            />
          ))
        : null}
      {offerPhone ? (
        phoneReady && prices.phone_reveal != null ? (
          (settled('phone', 'phone number') ?? (
            <RevealButton
              kind="phone"
              label="Access phone"
              tip={`Uses ${formatCredits(prices.phone_reveal)} per phone.`}
              held={reach.available.phone}
              pending={pendingKind === 'phone'}
              disabled={reveal.isPending}
              onReveal={() => run('phone')}
            />
          ))
        ) : (
          <RevealButton
            kind="phone"
            label="Access phone"
            tip="Phone numbers coming soon."
            disabled
          />
        )
      ) : null}
      {offerProfile && prices.profile_reveal != null
        ? reach.revealed.includes('profile')
          ? null
          : (settled('profile', 'profile') ?? (
              <RevealButton
                kind="profile"
                label="Enrich profile"
                tip={`Uses ${formatCredits(prices.profile_reveal)} per profile.`}
                held={reach.available.profile}
                pending={pendingKind === 'profile'}
                disabled={reveal.isPending}
                onReveal={() => run('profile')}
              />
            ))
        : null}
    </span>
  );
}
